/**
 * Configuração de navegação — NOC v3 (UI Rework Global).
 *
 * Reorganiza a sidebar nos 6 grupos do briefing v3:
 *   OVERVIEW · OPERATIONS · FIRMWARE · MONITORING · AUTOMATION · ADMIN
 *
 * Cada item aponta para uma `view` que JÁ existe no App.jsx — o
 * rework NÃO cria páginas novas nem renomeia views, só reorganiza a
 * navegação. Backend, APIs e rotas intocados.
 *
 * Os ícones são SVG inline (sem dependência de biblioteca de
 * ícones), resolvidos pelo componente NavIcon.
 */

/**
 * Grupos de navegação. A ordem aqui é a ordem na sidebar.
 * Cada item: { view, labelKey, icon }.
 */
export const NAV_GROUPS = [
  {
    id: "overview",
    labelKey: "navgroup.overview",
    items: [
      { view: "dashboard", labelKey: "nav.dashboard", icon: "grid", role: "super_admin" },
      { view: "fleet", labelKey: "nav.fleetMap", icon: "map", role: "super_admin" },
    ],
  },
  {
    id: "operations",
    labelKey: "navgroup.operations",
    items: [
      { view: "devices", labelKey: "nav.devices", icon: "device" },
      { view: "provisioning", labelKey: "nav.provisioning", icon: "qr", role: "super_admin" },
      { view: "installations", labelKey: "nav.fieldOps", icon: "wrench", role: "super_admin" },
    ],
  },
  {
    id: "firmware",
    labelKey: "navgroup.firmware",
    items: [
      { view: "firmware", labelKey: "nav.firmware", icon: "chip", role: "super_admin" },
      { view: "ota", labelKey: "nav.ota", icon: "rocket", role: "super_admin" },
      { view: "production", labelKey: "nav.production", icon: "box", role: "super_admin" },
      { view: "rollouts", labelKey: "nav.rollouts", icon: "layers", role: "super_admin" },
    ],
  },
  {
    id: "monitoring",
    labelKey: "navgroup.monitoring",
    items: [
      { view: "health", labelKey: "nav.health", icon: "heart" },
      { view: "observability", labelKey: "nav.observability", icon: "pulse", role: "super_admin" },
      { view: "alerts", labelKey: "nav.alerts", icon: "bell", badge: "alerts", role: "super_admin" },
      { view: "audit", labelKey: "nav.audit", icon: "list", role: "super_admin" },
    ],
  },
  {
    id: "automation",
    labelKey: "navgroup.automation",
    items: [
      { view: "rules", labelKey: "nav.rules", icon: "filter", role: "super_admin" },
      { view: "automation", labelKey: "nav.playbooks", icon: "bolt", role: "super_admin" },
    ],
  },
  {
    id: "admin",
    labelKey: "navgroup.admin",
    items: [
      // SAAS-PORTAL — inventário é plataforma (estoque global) → só super_admin.
      { view: "inventory", labelKey: "nav.inventory", icon: "box", role: "super_admin" },
      { view: "teams", labelKey: "nav.teams", icon: "users", role: "super_admin" },
      { view: "templates", labelKey: "nav.templates", icon: "layers", role: "super_admin" },
      { view: "accounts", labelKey: "nav.accounts", icon: "users", role: "super_admin" },
      { view: "tenants", labelKey: "nav.companies", icon: "building", role: "super_admin" },
      { view: "tenant-approval", labelKey: "nav.tenantApproval", icon: "building", role: "super_admin" },
      { view: "settings", labelKey: "nav.settings", icon: "gear" },
    ],
  },
  {
    // SAAS-PORTAL — grupo do PORTAL do cliente. Itens gated por capability de
    // tenant (owner/admin) — super_admin faz bypass. viewer/tenant_operator só
    // leem (Dispositivos/Saúde, itens sem gate acima).
    id: "portal",
    labelKey: "navgroup.portal",
    items: [
      { view: "claim-device", labelKey: "nav.addDevice", icon: "qr", cap: "devices:claim" },
      { view: "portal-users", labelKey: "nav.tenantUsers", icon: "users", cap: "tenant:manage" },
    ],
  },
];

/** Lista achatada de todos os itens — usada pela busca global. */
export const ALL_NAV_ITEMS = NAV_GROUPS.flatMap((g) =>
  g.items.map((it) => ({ ...it, groupKey: g.labelKey }))
);

/**
 * Quick actions — ações rápidas no topo. Cada uma navega para uma
 * view e, opcionalmente, dispara um efeito (tratado no AppShell).
 */
export const QUICK_ACTIONS = [
  { id: "qa-provisioning", view: "provisioning",
    labelKey: "quick.newDevice", icon: "qr" },
  { id: "qa-alerts", view: "alerts",
    labelKey: "quick.alerts", icon: "bell" },
  { id: "qa-fleet", view: "fleet",
    labelKey: "quick.map", icon: "map" },
  { id: "qa-automation", view: "automation",
    labelKey: "quick.automation", icon: "bolt" },
];
